<script>
    import { createEventDispatcher } from 'svelte';
  
    // Initialize the dispatcher for sending events to the parent
    const dispatch = createEventDispatcher();
  
    // Props
    export let availableSlots = [];
    
    // State
    let selectedSlot = null;
    let groupedSlots = {};
    
    // Group slots by actual date for better display
    $: {
        groupedSlots = {};
        availableSlots.forEach(slot => {
            // Create a unique key combining date and day name
            const dateKey = `${slot.date}|${slot.day_name}`;
            if (!groupedSlots[dateKey]) {
                groupedSlots[dateKey] = {
                    date: slot.date,
                    day_name: slot.day_name,
                    slots: []
                };
            }
            groupedSlots[dateKey].slots.push(slot);
        });
        
        // Sort slots by time within each date group
        Object.keys(groupedSlots).forEach(dateKey => {
            groupedSlots[dateKey].slots.sort((a, b) => a.start_time.localeCompare(b.start_time));
        });
        
        // Sort date groups chronologically (by actual date)
        groupedSlots = Object.fromEntries(
            Object.entries(groupedSlots).sort(([a], [b]) => {
                const dateA = groupedSlots[a].date;
                const dateB = groupedSlots[b].date;
                return dateA.localeCompare(dateB);
            })
        );
    }
    
    function selectSlot(slot) {
        selectedSlot = slot;
    }
    
    function handleSubmit() {
        if (!selectedSlot) {
            alert('Prašome pasirinkti laiką');
            return;
        }
        
        // Emit the selected datetime to the parent in the expected format
        dispatch('submit', { datetime: selectedSlot.datetime_start });
    }
    
    function translateDayName(englishDayName) {
        const dayNames = {
            'Monday': 'Pirmadienis',
            'Tuesday': 'Antradienis', 
            'Wednesday': 'Trečiadienis',
            'Thursday': 'Ketvirtadienis',
            'Friday': 'Penktadienis',
            'Saturday': 'Šeštadienis',
            'Sunday': 'Sekmadienis'
        };
        
        return dayNames[englishDayName] || englishDayName;
    }
    
    function formatDate(dateStr) {
        const date = new Date(dateStr);
        const formattedDate = date.toLocaleDateString('lt-LT', { 
            month: 'short', 
            day: 'numeric'
        });
        
        return formattedDate;
    }
    
    function formatDateForSlot(dateStr) {
        const date = new Date(dateStr);
        return date.toLocaleDateString('lt-LT', { 
            month: 'numeric', 
            day: 'numeric'
        });
    }
</script>

<div class="timeslots-container">
    <h4>Pasirinkite naują pamokos laiką</h4>
    <p class="info-text">Rodomi tik laisvi mokytojo laikai per artimiausių 11 dienų. Jeigu nerandate patogaus laiko galite užpildyti naują pamokos registraciją arba susisiekti su mumis tiesiogiai.</p>
    
    {#if Object.keys(groupedSlots).length === 0}
        <div class="no-slots">
            <p>Nėra prieinamų laikų artimiausių 11 dienų. Prašome susisiekti su mokytoju dėl kitų galimybių.</p>
        </div>
    {:else}
        <div class="slots-grid">
            {#each Object.entries(groupedSlots) as [dateKey, dateGroup]}
                <div class="day-section">
                    <h5 class="day-title">{translateDayName(dateGroup.day_name)} - {formatDate(dateGroup.date)}</h5>
                    <div class="day-slots">
                        {#each dateGroup.slots as slot}
                            <button 
                                class="slot-button {selectedSlot === slot ? 'selected' : ''}"
                                on:click={() => selectSlot(slot)}
                            >
                                <div class="slot-time">{slot.start_time} - {slot.end_time}</div>
                            </button>
                        {/each}
                    </div>
                </div>
            {/each}
        </div>
        
        <div class="submit-section">
            {#if selectedSlot}
                <div class="selected-info">
                    <strong>Pasirinktas laikas:</strong> 
                    {translateDayName(selectedSlot.day_name)}, {formatDate(selectedSlot.date)} {selectedSlot.start_time} - {selectedSlot.end_time}
                </div>
            {/if}
            
            <button 
                class="submit-button"
                on:click={handleSubmit}
                disabled={!selectedSlot}
            >
                Patvirtinti pakeitimą
            </button>
        </div>
    {/if}
</div>

<style>
    .timeslots-container {
        padding: 15px;
        background-color: #f9f9f9;
        border-radius: 8px;
        margin: 10px 0;
    }
    
    .timeslots-container h4 {
        margin: 0 0 10px 0;
        color: #14695b;
        font-size: 1.1em;
    }
    
    .info-text {
        margin: 0 0 20px 0;
        color: #666;
        font-size: 0.9em;
        font-style: italic;
    }
    
    .no-slots {
        text-align: center;
        padding: 20px;
        background-color: #fff3cd;
        border: 1px solid #ffeaa7;
        border-radius: 4px;
        color: #856404;
    }
    
    .slots-grid {
        display: flex;
        flex-direction: column;
        gap: 15px;
    }
    
    .day-section {
        background-color: white;
        border-radius: 6px;
        padding: 12px;
        box-shadow: 0px 1px 3px rgba(0,0,0,0.1);
    }
    
    .day-title {
        margin: 0 0 10px 0;
        color: #333;
        font-size: 1em;
        font-weight: 600;
        border-bottom: 1px solid #eee;
        padding-bottom: 5px;
    }
    
    .day-slots {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
        gap: 8px;
    }
    
    .slot-button {
        background: white;
        border: 2px solid #ddd;
        border-radius: 6px;
        padding: 8px;
        cursor: pointer;
        transition: all 0.2s ease;
        text-align: center;
        min-height: 50px;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }
    
    .slot-button:hover {
        border-color: #fab45a;
        background-color: #fef9f3;
    }
    
    .slot-button.selected {
        border-color: #14695b;
        background-color: #e8f5f3;
        color: #14695b;
    }
    
    .slot-date {
        font-size: 0.8em;
        color: #666;
        margin-bottom: 2px;
    }
    
    .slot-time {
        font-weight: 600;
        font-size: 0.9em;
    }
    
    .submit-section {
        margin-top: 20px;
        padding-top: 15px;
        border-top: 1px solid #eee;
    }
    
    .selected-info {
        margin-bottom: 15px;
        padding: 10px;
        background-color: #e8f5f3;
        border-radius: 4px;
        color: #14695b;
        font-size: 0.9em;
    }
    
    .submit-button {
        width: 100%;
        font-weight: 700;
        height: 50px;
        background-color: #fab45a;
        border: 1px solid #000000;
        box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s ease;
    }
    
    .submit-button:hover:not(:disabled) {
        background-color: #14695b;
        color: white;
    }
    
    .submit-button:disabled {
        background-color: #ccc;
        border-color: #999;
        cursor: not-allowed;
        opacity: 0.6;
    }
</style>
